// Cluster the raw screener level map (ScreenerLevelMap: supports/resistances/flipped
// + sessionATR) into confluence zones by ATR proximity. Ported from data.js.
import { levelLabel } from './level-label';

// strength 0–1 from tier (1 strongest) + weight (0–6)
export function levelStrength(l: any): number {
  return Math.min(1, ((l.weight || 0) / 6) * 0.68 + ((4 - (l.tier || 3)) / 3) * 0.32);
}

export function clusterStructure(struct: any, lastPrice: number): { zones: any[]; atr: number } {
  if (!struct) return { zones: [], atr: 0.01 };
  const atr = struct.sessionATR || 0.01;
  const merge = (arr: any[], role: string) => {
    const sorted = (arr || []).slice().sort((a, b) => a.price - b.price);
    const zones: any[] = [];
    sorted.forEach((l) => {
      const z = zones[zones.length - 1];
      if (z && Math.abs(l.price - z._lastPx) <= atr * 0.4) { z.members.push(l); z._lastPx = l.price; }
      else zones.push({ role, members: [l], _lastPx: l.price });
    });
    return zones.map((z, i) => {
      const wsum = z.members.reduce((a: number, b: any) => a + (b.weight || 0), 0) || 1;
      const price = z.members.reduce((a: number, b: any) => a + b.price * ((b.weight || 0) + 1), 0)
        / z.members.reduce((a: number, b: any) => a + ((b.weight || 0) + 1), 0);
      const lo = Math.min(...z.members.map((x: any) => x.price));
      const hi = Math.max(...z.members.map((x: any) => x.price));
      const minTier = Math.min(...z.members.map((x: any) => x.tier || 3));
      const statusRank: Record<string, number> = { DEFENDED: 5, AT: 4, WATCHING: 3, BROKEN: 1, LOST: 1 };
      const status = z.members.slice().sort((a: any, b: any) => (statusRank[b.status] || 2) - (statusRank[a.status] || 2))[0].status;
      const strength = Math.max(...z.members.map(levelStrength));
      return {
        id: role[0] + 'Z' + i, role, price: +price.toFixed(4), lo, hi,
        members: z.members.sort((a: any, b: any) => (b.weight || 0) - (a.weight || 0)),
        weightSum: wsum, tier: minTier, confluence: z.members.length, status, strength,
      };
    });
  };
  const sZ = merge((struct.supports || []).filter((l: any) => l.price < lastPrice + atr * 0.5), 'support');
  const rZ = merge(struct.resistances || [], 'resistance');
  const fZ = (struct.flipped || []).map((f: any, i: number) => ({
    id: 'fZ' + i, role: 'flipped', price: f.price, lo: f.price, hi: f.price, members: [f],
    weightSum: 3, tier: 1, confluence: f.timesTestedAsSupport || 1, status: f.status, strength: 0.6,
  }));
  return { zones: [...rZ, ...fZ, ...sZ].sort((a, b) => b.price - a.price), atr };
}

// Wrap a single raw level as a one-member "zone" so the 'all' and 'cluster'
// modes render through the exact same StructureMap path.
function levelToItem(l: any) {
  const flip = l.role === 'flipped' || l.side === 'flipped';
  return {
    id: l.id, role: flip ? 'flipped' : (l.side === 'resistance' ? 'resistance' : 'support'),
    price: l.price, lo: l.price, hi: l.price,
    members: [l],
    weightSum: flip ? 3 : (l.weight || 0),
    tier: flip ? 1 : (l.tier || 3),
    confluence: flip ? (l.timesTestedAsSupport || 1) : 1,
    status: l.status,
    strength: flip ? 0.6 : levelStrength(l),
  };
}

// Truth-expiry for dead structure: a zone whose every member is LOST/BROKEN
// used to fade to 12% opacity and sit there forever. Dead zones get a grace
// window (the "it just broke" context is real information), then leave.
//
// The grace clock is CLIENT-side: projected members often carry no death
// timestamp at all (a resistance that breaks on first touch has no
// lastRejectionAt; worker snapshots don't track lastTouchAt), and keying the
// window on last-activity would hide exactly the just-broke-watch-the-retest
// zones the grace exists for. So the first render that SEES a zone dead
// starts its clock; a zone observed dead longer than the TTL leaves. A zone
// that comes back to life clears its clock.
const DEAD_ZONE_TTL_MS = 15 * 60 * 1000;
const deadSeenAt = new Map<string, number>();
function zoneAlive(z: any, symbol: string): boolean {
  // Identity must be PRICE-STABLE and SYMBOL-SCOPED. Member ids embed the
  // level's current price (type:price), and dynamic levels — VWAP, EMAs —
  // keep drifting price on every snapshot even while LOST; a price-embedding
  // key would mint a fresh key (and a fresh 15-min clock) every snapshot, so
  // the most common dead structure would never expire. Level TYPE strings are
  // stable across price drift and unique per side for everything except
  // multi-instance kinds (retained swings, walls), whose PRICE is itself the
  // stable identity — so: type for dynamic levels, id for price-keyed ones is
  // wrong; use type when present, else price. A 1% geometric price bucket
  // disambiguates multi-instance types (two walls / two retained swings at
  // different prices share a type string) while tolerating the small drift a
  // dead dynamic level still accretes — a full 1% drift resets the clock
  // once, vs every snapshot with exact prices. Symbol prefix stops the main
  // pane and detached windows (different symbols, shared module state) from
  // colliding on identical type:price keys.
  const bucket = (p: number) => (p > 0 ? Math.round(Math.log(p) / 0.00995) : 0); // ~1% geometric
  const key = symbol + '|' + z.role + '|'
    + z.members.map((m: any) => (m.type ?? 'px') + '@' + bucket(m.price)).sort().join(',');
  const dead = z.members.every((m: any) => m.status === 'LOST' || m.status === 'BROKEN');
  const now = Date.now();
  if (!dead) { deadSeenAt.delete(key); return true; }
  let since = deadSeenAt.get(key);
  if (since === undefined) {
    since = now;
    deadSeenAt.set(key, since);
    // Bound the ledger: entries for zones that stopped rendering entirely
    // (symbol switch, level churn) are dropped once stale past the TTL.
    if (deadSeenAt.size > 800) {
      for (const [k, t] of deadSeenAt) { if (now - t > DEAD_ZONE_TTL_MS) deadSeenAt.delete(k); }
    }
  }
  // Prefer a real member timestamp when one exists and is FRESHER than our
  // first-seen mark (a re-test during the grace window extends it).
  const lastTested = Math.max(0, ...z.members.map((m: any) => m.lastTestedAt ?? 0));
  return now - Math.max(since, lastTested) <= DEAD_ZONE_TTL_MS;
}

// ============================================================================
// The model's reads on the rail.
//
// MV-5 scores EVERY level on the minute's map (≤ 384) and the vision frame
// carries all of them as `mv5Board` (vision-service mv5.ts mv5BoardWire). The
// frame's own `levels` rows are only the rules rail (~12 nearest price) plus
// the structural rows near the close, so a rail fed from them numbered 15 of
// 47 Main zones on the five live symbols of 2026-10-12 — the board numbers 41.
// `railReads` takes the board when the frame has one (the MV-5 lane) and the
// rows otherwise (MV-4).
//
// Each read carries P(hold) — MV-5's 1m hold, the owner's display default —
// and, for MV-5's FIXED levels only (mv-core MV5_READER_FAMILIES), P(reach):
// will price get there within 30m / 1h / by the close. MAs and VWAP have no
// reach read: that head was never trained on moving levels.
// ============================================================================

/** The reach horizon the rail shows: the worker's target rule default (picker-engine MV5_TARGET_REACH_HORIZON). */
export const RAIL_REACH_HORIZON = '60m';
/** At or above this the reach read is drawn solid — the worker's target cut (MV5_TAU_TARGET_REACH). */
export const RAIL_REACH_SOLID = 0.5;

/** A model read the rail joins onto a zone member of the same class at the same price. */
export interface RailRead {
  family: string;
  price: number;
  /** P(hold) — MV-5: the 1m hold; MV-4: its calibrated p */
  p: number;
  /** MV-5: P(hold) per label timeframe (1m 3m 5m 15m) */
  hold?: Record<string, number> | null;
  /** MV-5: P(reach) within 30m / 60m / by the close (eod) — fixed levels only, else null */
  reach?: Record<string, number> | null;
  /** the level's type in the projected-level vocabulary ('1D_SwingLow', '5m_EMA9', 'SessionHigh', …) */
  type?: string;
  /** the rail class it joins on (levelClass) */
  cls?: LevelClass;
  /** short label (level-label.ts) */
  ind?: string;
  /** 'mv5' = MV-5's read: MV-4's display-hidden families (MV_P_HIDE) do not apply */
  src?: 'mv5';
}

// MV-5 board ids → the projected-level type the rail draws for the same level.
//   ST:1D:0.6900 → 1D_SwingLow (by side)   S:5m_EMA9 / R:SessionHigh → after the colon
//   F:<px> → FLIPPED   W:buy|sell:<px> → WALL_1M   RET:<side>:<px> → none (a retained swing
//   keeps no timeframe; the map projects it as a <tf>_Swing{High|Low}, i.e. class 'swing')
function boardType(id: string, side: string): string | undefined {
  const st = /^ST:(1D|1W):/.exec(id);
  if (st) return st[1] + (side === 'S' ? '_SwingLow' : '_SwingHigh');
  if (/^F:/.test(id)) return 'FLIPPED';
  if (/^W:/.test(id)) return 'WALL_1M';
  if (/^RET:/.test(id)) return undefined;
  const sr = /^[SR]:(.+)$/.exec(id);
  return sr ? sr[1] : undefined;
}

function readClass(family: string, type?: string): LevelClass {
  if (family === 'retained') return 'swing';
  if (family === 'flip') return 'flipped';
  if (family === 'wall') return 'wall';
  return levelClass({ type });
}

function readLabel(family: string, type?: string): string {
  if (family === 'retained' || family === 'flip' || family === 'wall') return levelLabel(undefined, family);
  return levelLabel(type, family) || family;
}

const isMv5Frame = (frame: any): boolean => /^mv5/i.test(String(frame?.model?.version ?? frame?.mv1?.model ?? ''));

/** MV-5's board as rail reads: one per scored level, in token order. Pure. */
export function mv5BoardReads(board: any): RailRead[] {
  if (!board || !Array.isArray(board.ids) || !Array.isArray(board.price)) return [];
  const n = Math.min(Number(board.n) || 0, board.ids.length, board.price.length);
  const hold = board.hold || {};
  const reach = board.reach || {};
  const out: RailRead[] = [];
  for (let k = 0; k < n; k++) {
    const price = Number(board.price[k]);
    const p = hold['1m'] ? hold['1m'][k] : null;
    if (!(price > 0) || typeof p !== 'number') continue;
    const family = String((board.fams || [])[(board.fam || [])[k]] ?? '');
    const type = boardType(String(board.ids[k]), String(board.side || '')[k] || 'R');
    const h: Record<string, number> = {};
    for (const tf of ['1m', '3m', '5m', '15m']) {
      const v = hold[tf] ? hold[tf][k] : null;
      if (typeof v === 'number') h[tf] = v;
    }
    let r: Record<string, number> | null = null;
    for (const hz of ['30m', '60m', 'eod']) {
      const v = reach[hz] ? reach[hz][k] : null;
      if (typeof v === 'number') { if (!r) r = {}; r[hz] = v; }
    }
    out.push({
      family, price, p, hold: h, reach: r, ...(type !== undefined ? { type } : {}),
      cls: readClass(family, type), ind: readLabel(family, type), src: 'mv5',
    });
  }
  return out;
}

/** The reads a vision frame gives the rail: MV-5's whole board when the frame carries one, else its level rows. */
export function railReads(frame: any): RailRead[] {
  if (!frame) return [];
  if (frame.mv5Board) return mv5BoardReads(frame.mv5Board);
  const mv5 = isMv5Frame(frame);
  return (frame.levels || [])
    .filter((l: any) => typeof l.p === 'number' && l.price > 0)
    .map((l: any) => {
      const family = String(l.family ?? '');
      return {
        family, price: l.price, p: l.p, hold: l.hold ?? null, reach: l.reach ?? null, type: l.type,
        cls: readClass(family, l.type), ind: readLabel(family, l.type), ...(mv5 ? { src: 'mv5' as const } : {}),
      };
    });
}

/** P(hold) → display band. 0.35–0.50 is the model saying it does not know: drawn faded ('unsure'), never blank. */
export function holdBand(p: number): 'strong' | 'hold' | 'unsure' | 'paper' {
  return p >= 0.8 ? 'strong' : p >= 0.5 ? 'hold' : p <= 0.35 ? 'paper' : 'unsure';
}

// Join the model's reads onto a zone. A read lands on a zone only through a
// MEMBER of the same class at that member's price (fixed levels tight, moving
// ones at the MA-glue scale), so the number on a shelf is that shelf's own —
// a hidden moving average sitting inside a daily shelf can no longer speak for
// it. Hold: the zone's most decisive read (max |p − 0.5|), as before. Reach:
// the zone's most reachable read (its nearest fixed level), shown on the side
// the trade is pointed at — above price when long, below when short.
// MV_P_HIDE: MV-4 measured these families miscalibrated, so MV-4's reads of
// them never annotate. MV-5 was never measured that way: its reads show.
const MV_P_HIDE = new Set(['wall', 'retained', 'session', 'flip']);
const DRIFT_CLS: ReadonlySet<string> = new Set(['ma', 'vwap', 'session']);

// The reads indexed by class, each list price-sorted — built once per frame (the rail recomputes on every price
// update; a board is ≤ 384 reads), then each member finds its reads by binary search.
const readIndexCache = new WeakMap<object, Map<string, any[]>>();
function readIndex(visionLevels: any[]): Map<string, any[]> {
  const cached = readIndexCache.get(visionLevels);
  if (cached) return cached;
  const byCls = new Map<string, any[]>();
  for (const r of visionLevels) {
    if (!r || typeof r.p !== 'number' || !(r.price > 0)) continue;
    if (r.src !== 'mv5' && MV_P_HIDE.has(r.family)) continue;
    const cls = r.cls ?? readClass(String(r.family ?? ''), r.type);
    const list = byCls.get(cls);
    if (list) list.push(r); else byCls.set(cls, [r]);
  }
  for (const list of byCls.values()) list.sort((a, b) => a.price - b.price);
  readIndexCache.set(visionLevels, byCls);
  return byCls;
}

function mvAnnotate(zones: any[], visionLevels: any[] | undefined, lastPrice: number, side: 'long' | 'short'): void {
  if (!visionLevels || !visionLevels.length) return;
  const byCls = readIndex(visionLevels);
  for (const z of zones) {
    const hits = new Set<any>();
    for (const m of z.members || []) {
      const cls = m.cls ?? levelClass(m);
      const list = byCls.get(cls);
      if (!list || !(m.price > 0)) continue;
      const tol = DRIFT_CLS.has(cls) ? Math.max(1e-3, m.price * 4e-3) : Math.max(1e-4, m.price * 5e-4);
      let lo = 0, hi = list.length;
      while (lo < hi) { const mid = (lo + hi) >> 1; if (list[mid].price < m.price - tol) lo = mid + 1; else hi = mid; }
      for (let i = lo; i < list.length && list[i].price <= m.price + tol; i++) hits.add(list[i]);
    }
    if (!hits.size) continue;
    let best: any = null;
    let reachBest: any = null;
    for (const r of hits) {
      if (!best || Math.abs(r.p - 0.5) > Math.abs(best.p - 0.5)) best = r;
      const rp = r.reach ? r.reach[RAIL_REACH_HORIZON] : undefined;
      if (typeof rp === 'number' && (!reachBest || rp > reachBest.reach[RAIL_REACH_HORIZON])) reachBest = r;
    }
    const ind = best.ind ?? readLabel(String(best.family ?? ''), best.type);
    const rp = reachBest ? reachBest.reach[RAIL_REACH_HORIZON] : null;
    z.mv = {
      p: best.p, pct: Math.round(best.p * 100), band: holdBand(best.p), side: best.side, ind,
      hold: best.hold ?? null, src: best.src ?? null,
      reach: reachBest ? {
        p: rp, pct: Math.round(rp * 100), at: reachBest.price, all: reachBest.reach,
        ind: reachBest.ind ?? readLabel(String(reachBest.family ?? ''), reachBest.type),
      } : null,
      // the side the trade is pointed at: targets live above price when long, below when short
      target: lastPrice > 0 && (side === 'short' ? z.price < lastPrice : z.price > lastPrice),
    };
  }
}

// Build the render-ready structure view consumed by the StructureMap.
//   mode 'cluster' → nearby levels merged into confluence zones (bands)
//   mode 'near'    → cluster, then only what's reachable: zones within 6·ATR of
//                    price, plus heavyweight tier-1 structure and the active
//                    floor/ceiling (kept even when far — a gapper's floor can
//                    legitimately sit 8 ATR below)
//   mode 'all'     → every level kept distinct (single-member zone)
// All share one shape; the StructureMap renders them identically. Also returns
// the active floor (nearSup) + ceiling (nearRes) bracketing the last price.
export function structureView(struct: any, lastPrice: number, mode?: string, visionLevels?: any[], prefs?: LevelPrefs | null, side?: string | null) {
  struct = applyLevelPrefs(struct, prefs);
  const tradeSide = side === 'short' ? 'short' : 'long';
  if (mode === 'main') {
    // Main returns its own zone set, so it has to be annotated here: the
    // `mvAnnotate` call further down is BELOW this early return and never ran
    // for the default view. Every rack rendered in Main — the one almost
    // everyone is looking at — carried no model read at all, while cluster /
    // near / model modes showed them, which is exactly how this hid.
    const v = mainView(struct, lastPrice, prefs || LEVEL_PREFS_DEFAULT);
    mvAnnotate(v.zones, visionLevels, lastPrice, tradeSide);
    return v;
  }
  const cl = clusterStructure(struct, lastPrice);
  const atr = cl.atr;
  let zones: any[];
  if (mode === 'all') {
    const sup = (struct.supports || []).filter((l: any) => l.price < lastPrice + atr * 0.5).map(levelToItem);
    const res = (struct.resistances || []).map(levelToItem);
    const fl = (struct.flipped || []).map(levelToItem);
    zones = [...res, ...fl, ...sup].sort((a, b) => b.price - a.price);
  } else {
    zones = cl.zones;
  }
  const sym = struct.symbol || 'mock';
  zones = zones.filter((z) => zoneAlive(z, sym));
  const nearSup = zones.filter((z) => z.role !== 'resistance' && z.price < lastPrice).sort((a, b) => b.price - a.price)[0] || null;
  const nearRes = zones.filter((z) => z.role === 'resistance' && z.price > lastPrice).sort((a, b) => a.price - b.price)[0] || null;
  if (mode === 'near') {
    const maxDist = atr * 6;
    zones = zones.filter((z) =>
      Math.abs(z.price - lastPrice) <= maxDist
      || (z.tier === 1 && z.weightSum >= 5)
      || z === nearSup || z === nearRes);
  }
  mvAnnotate(zones, visionLevels, lastPrice, tradeSide);
  if (mode === 'model') {
    // The model runs the rack: most decisive reads first (a 97% shelf or a
    // 12% paper wall both matter), unscored zones sink, capped so the column
    // stays readable. The active floor/ceiling always keep their seats.
    const score = (z: any) => (z.mv && z.mv.band ? Math.abs(z.mv.p - 0.5) : -1);
    const ranked = [...zones].sort((a, b) => score(b) - score(a));
    const keep = new Set(ranked.slice(0, 12));
    if (nearSup) keep.add(nearSup);
    if (nearRes) keep.add(nearRes);
    zones = zones.filter((z) => keep.has(z));
  }
  return { zones, nearSup, nearRes, atr };
}

// ============================================================================
// Level display preferences + the MAIN mode (default).
//
// The level map carries ~200 entries per symbol (VEEA 2026-09-15: 32 moving
// averages, 172 daily/weekly structural swings, walls, flipped, retained).
// Drawing them all is a dump; the AI/Zones/Near modes only thin it by distance
// or model read. Main answers "what are the few levels that matter here":
//
//   1. Moving averages never draw (kept as chart lines); LOST/BROKEN never draw.
//   2. Daily/weekly structural swings are clustered into SHELVES (within 3%) —
//      12 swings between 3.82 and 4.20 are one band, not twelve lines.
//   3. Per side, in priority order, capped at `perSide`:
//        nearest zone (floor / ceiling) · session extreme · prior-day H/L ·
//        one proven intraday level within 6 ATR (wall / swing / flipped / VWAP
//        with ≥1 rejection or defense) · the next three shelves · the first
//        weekly shelf · the first shelf beyond the session run-high / base-low
//        (so a runner is never blind above and always has a floor).
//   4. Resistance zones carry an EXTENSION tag: the run from the session base
//      the stock would have made when it reaches that level. On the three
//      symbol-days audited (VEEA/YFOR/TPST 2026-09-15) candidate-level touches
//      reached after a ≥75% run rejected 94-100% of the time, <25% almost
//      never (7%). It is a prior for visibility, not a signal; the tape still
//      decides the entry. See architecture/handoff/memory/veea_short_level_method.md.
//
// The class toggles apply to EVERY mode (the Levels popover in the chart
// header): untick "Moving averages" and the AI/Zones/Near/All modes lose them too.
// ============================================================================
export type LevelClass = 'structural' | 'prior' | 'session' | 'wall' | 'flipped' | 'swing' | 'vwap' | 'ma' | 'other';

export interface LevelPrefs {
  structural: boolean; prior: boolean; session: boolean; wall: boolean; flipped: boolean;
  swing: boolean; vwap: boolean; ma: boolean;
  perSide: number;   // Main mode: max zones drawn per side
  shortTag: boolean; // Main mode: SHORT / WATCH extension tag on resistance zones
}

export const LEVEL_PREFS_DEFAULT: LevelPrefs = {
  structural: true, prior: true, session: true, wall: true, flipped: true, swing: true, vwap: true, ma: false,
  perSide: 5, shortTag: true,
};

// Classify a projected level (realtime level-projection shape) or a raw map entry by its type string.
export function levelClass(l: any): LevelClass {
  const t = String((l && l.type) || '');
  if (!l) return 'other';
  if (l.side === 'flipped' || l.role === 'flipped' || t === 'FLIPPED') return 'flipped';
  if (/^1[DW]_Swing(High|Low)$/.test(t)) return 'structural';
  if (/^Prior(Day|Week)(High|Low)$/.test(t)) return 'prior';
  if (/^Session/.test(t)) return 'session';
  if (/^WALL_/.test(t) || l.timeframe === 'wall') return 'wall';
  if (t === 'VWAP') return 'vwap';
  if (/EMA|SMA/.test(t)) return 'ma';
  if (/Swing(High|Low)$/.test(t)) return 'swing';
  return 'other';
}

// Drop the level kinds the operator has turned off. Applies before any mode logic.
export function applyLevelPrefs(struct: any, prefs?: LevelPrefs | null) {
  if (!struct || !prefs) return struct;
  const keep = (l: any) => { const c = levelClass(l); return c === 'other' ? true : !!(prefs as any)[c]; };
  return {
    ...struct,
    supports: (struct.supports || []).filter(keep),
    resistances: (struct.resistances || []).filter(keep),
    flipped: prefs.flipped ? (struct.flipped || []) : [],
  };
}

const STATUS_RANK: Record<string, number> = { DEFENDED: 5, AT: 4, WATCHING: 3, BROKEN: 1, LOST: 1 };

// Merge price-sorted levels into zones whose neighbours sit within `band(px)`;
// same output shape as clusterStructure so the StructureMap renders them unchanged.
// `maxWidth` (fraction of the shelf's floor) stops a dense ladder chaining into one
// band — 26 weekly swings 1-3% apart would otherwise merge into a 9.35-11.50 "shelf".
function mergeBand(levels: any[], role: string, band: (px: number) => number, idPrefix: string, maxWidth?: number): any[] {
  const sorted = levels.slice().sort((a, b) => a.price - b.price);
  const groups: any[] = [];
  sorted.forEach((l) => {
    const g = groups[groups.length - 1];
    const fits = g && Math.abs(l.price - g.lastPx) <= band(l.price) && (!maxWidth || l.price <= g.firstPx * (1 + maxWidth));
    if (fits) { g.members.push(l); g.lastPx = l.price; }
    else groups.push({ members: [l], lastPx: l.price, firstPx: l.price });
  });
  return groups.map((g, i) => {
    const m = g.members;
    const wsum = m.reduce((a: number, b: any) => a + (b.weight || 0), 0) || 1;
    const price = m.reduce((a: number, b: any) => a + b.price * ((b.weight || 0) + 1), 0) / m.reduce((a: number, b: any) => a + ((b.weight || 0) + 1), 0);
    const status = m.slice().sort((a: any, b: any) => (STATUS_RANK[b.status] || 2) - (STATUS_RANK[a.status] || 2))[0].status;
    return {
      id: idPrefix + i, role, price: +price.toFixed(4),
      lo: Math.min(...m.map((x: any) => x.price)), hi: Math.max(...m.map((x: any) => x.price)),
      members: m.slice().sort((a: any, b: any) => (b.weight || 0) - (a.weight || 0)),
      weightSum: wsum, tier: Math.min(...m.map((x: any) => x.tier || 3)), confluence: m.length, status,
      strength: Math.max(...m.map(levelStrength)),
      touches: m.reduce((a: number, b: any) => a + (b.touches ?? b.touchCount ?? b.rejections ?? 0), 0),
      weekly: m.some((x: any) => /^1W_/.test(String(x.type || ''))),
    };
  });
}

export function mainView(struct: any, lastPrice: number, prefs: LevelPrefs) {
  const atr = struct.sessionATR || 0.01;
  const baseLow = Number(struct.sessionBaseLow) > 0 ? Number(struct.sessionBaseLow) : null;
  const runHigh = Number(struct.sessionRunHigh) > 0 ? Number(struct.sessionRunHigh) : null;
  const alive = (l: any) => l && l.status !== 'LOST' && l.status !== 'BROKEN';
  const all: any[] = [];
  for (const l of struct.supports || []) if (alive(l)) all.push({ ...l, cls: levelClass(l) });
  for (const l of struct.resistances || []) if (alive(l)) all.push({ ...l, cls: levelClass(l) });
  for (const f of struct.flipped || []) if (alive(f)) all.push({ ...f, type: f.type || 'FLIPPED', cls: 'flipped', weight: f.weight ?? 3, tier: f.tier ?? 1, touches: f.touches ?? f.timesTestedAsSupport ?? 0 });
  const perSide = Math.max(1, Math.min(10, Number(prefs.perSide) || 5));
  const out: any[] = [];
  let nearSup: any = null, nearRes: any = null;
  for (const side of ['resistance', 'support']) {
    const up = side === 'resistance';
    // Side by PRICE, not by the map's stale side flag (a weekly swing-high slot can sit 70% below price after a run).
    const mine = all.filter((l) => (up ? l.price > lastPrice : l.price < lastPrice));
    const shelves = mergeBand(mine.filter((l) => l.cls === 'structural'), side, (px) => Math.max(atr * 0.4, px * 0.03), (up ? 'mR' : 'mS') + 'sh', 0.08);
    const zones = mergeBand(mine.filter((l) => l.cls !== 'structural'), side, () => atr * 0.4, (up ? 'mR' : 'mS') + 'z');
    for (const z of shelves) z.cls = 'structural';
    for (const z of zones) { z.cls = z.members[0].cls; if (z.members.every((m: any) => m.cls === 'flipped')) z.role = 'flipped'; }
    const everything = [...shelves, ...zones];
    for (const z of everything) {
      z.dist = Math.abs(z.price - lastPrice) / atr;
      const base = z.weekly ? 6 : (z.cls === 'structural' || z.cls === 'prior') ? 5 : (z.cls === 'wall' || z.cls === 'session' || z.cls === 'flipped') ? 4 : z.cls === 'vwap' ? 3 : (z.members[0].weight || 1);
      z.score = base + 2 * Math.min(z.touches || 0, 5);
    }
    const byDist = everything.slice().sort((a, b) => a.dist - b.dist);
    const picks = new Map<any, string>();
    const add = (z: any, why: string) => { if (z && !picks.has(z)) picks.set(z, why); };
    add(byDist[0], up ? 'nearest ceiling' : 'nearest floor');
    if (up) add(zones.find((z) => z.cls === 'session'), 'session high');
    else if (baseLow) add(byDist.find((z) => Math.abs(z.price - baseLow) <= atr * 0.4), 'session base low');
    add(zones.find((z) => z.members.some((m: any) => /^PriorDay/.test(String(m.type || '')))), up ? 'prior-day high' : 'prior-day low');
    add(zones.filter((z) => z.cls !== 'session' && z.cls !== 'prior' && z.dist <= 6 && (z.touches || 0) >= 1).sort((a, b) => b.score - a.score)[0], 'proven intraday level');
    const shelvesByDist = shelves.slice().sort((a, b) => a.dist - b.dist);
    shelvesByDist.slice(0, 3).forEach((z, i) => add(z, i === 0 ? 'next daily/weekly shelf' : 'shelf ' + (i + 1) + ' out'));
    add(shelvesByDist.find((z) => z.weekly), 'first weekly shelf');
    const extreme = up ? runHigh : baseLow;
    if (extreme) add(shelvesByDist.find((z) => (up ? z.price > extreme : z.price < extreme)), up ? 'first structure above the session high' : 'first structure below the session low');
    const chosen = Array.from(picks.entries()).slice(0, perSide).map(([z, why]) => Object.assign(z, { why }));
    for (const z of chosen) {
      // Tag only what is in reach today (≤ 6 session ATR); a SHORT chip on a shelf 11 ATR away is noise.
      if (up && prefs.shortTag && baseLow) { z.ext = z.price / baseLow - 1; z.tag = z.dist <= 6 ? (z.ext >= 0.75 ? 'short' : z.ext >= 0.5 ? 'watch' : null) : null; }
      out.push(z);
    }
    if (up) nearRes = byDist[0] || null; else nearSup = byDist[0] || null;
  }
  out.sort((a, b) => b.price - a.price);
  return { zones: out, nearSup, nearRes, atr };
}
